import { useI18n } from '@/shared/i18n';
import type { NavV2Item } from '@/shared/navigation/admin-v2-config';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useNavigate } from 'react-router-dom';

interface SidebarItemProps {
  item: NavV2Item;
  isActive: boolean;
  badge?: number;
  collapsed?: boolean;
  onClick?: () => void;
}

function Badge({ value }: { value: number }) {
  const isUrgent = value > 9;
  return (
    <span
      className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none min-w-[18px] text-center ${
        isUrgent
          ? 'bg-red-500/10 text-red-600 dark:text-red-400'
          : 'bg-primary-500/10 text-primary-600 dark:text-primary-400'
      }`}
    >
      {value > 99 ? '99+' : value}
    </span>
  );
}

function Dot({ urgent }: { urgent: boolean }) {
  return (
    <span
      className={`block w-1.5 h-1.5 rounded-full ${
        urgent ? 'bg-red-500' : 'bg-primary-500'
      }`}
    />
  );
}

function FloatingTooltip({
  label,
  top,
  left,
}: {
  label: string;
  top: number;
  left: number;
}) {
  return createPortal(
    <div
      role="tooltip"
      style={{ position: 'fixed', top, left, transform: 'translateY(-50%)' }}
      className="pointer-events-none z-[9999] px-2 py-1 rounded-md bg-theme border border-theme-light text-xs font-medium text-theme whitespace-nowrap shadow-md"
    >
      {label}
    </div>,
    document.body,
  );
}

export function SidebarItem({ item, isActive, badge, collapsed, onClick }: SidebarItemProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const label = t(item.labelKey);
  const hasBadge = badge !== undefined && badge > 0;
  const Icon = item.icon;

  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);

  const handleEnter = (e: React.SyntheticEvent<HTMLElement>) => {
    if (!collapsed) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPos({ top: rect.top + rect.height / 2, left: rect.right + 8 });
  };
  const handleLeave = () => setTooltipPos(null);

  const base = collapsed
    ? 'group relative w-full flex items-center justify-center h-10 rounded-xl transition-colors'
    : 'group relative w-full flex items-center justify-between gap-2 pl-3 pr-2.5 py-2 rounded-xl text-sm font-medium transition-colors';
  const active = 'bg-primary-500/10 text-primary-700 dark:text-primary-300';
  const inactive = 'text-theme-secondary hover:bg-surface-secondary hover:text-theme';

  const railClass = isActive
    ? 'absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-primary-500'
    : 'absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-transparent';

  const inner = collapsed ? (
    <>
      <span className={railClass} aria-hidden="true" />
      <Icon size={18} />
      {hasBadge && (
        <span className="absolute top-1.5 right-1.5">
          <Dot urgent={badge! > 9} />
        </span>
      )}
    </>
  ) : (
    <>
      <span className={railClass} aria-hidden="true" />
      <div className="flex items-center gap-2.5 min-w-0">
        <Icon size={16} className="flex-shrink-0" />
        <span className="truncate">{label}</span>
      </div>
      {item.comingSoon ? (
        <span className="text-[9px] font-semibold tracking-wide bg-primary-500/10 text-primary-600 dark:text-primary-400 px-1.5 py-0.5 rounded-full">
          {t('menu.v2.comingSoon')}
        </span>
      ) : hasBadge ? (
        <Badge value={badge!} />
      ) : null}
    </>
  );

  const tooltip = collapsed && tooltipPos ? (
    <FloatingTooltip label={label} top={tooltipPos.top} left={tooltipPos.left} />
  ) : null;

  if (item.comingSoon) {
    return (
      <>
        <button
          type="button"
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          onFocus={handleEnter}
          onBlur={handleLeave}
          onClick={() => {
            navigate(`/admin-v2/proximamente?key=${item.id}`);
            onClick?.();
          }}
          className={`${base} ${inactive}`}
        >
          {inner}
        </button>
        {tooltip}
      </>
    );
  }

  return (
    <>
      <NavLink
        to={item.path}
        onClick={onClick}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onFocus={handleEnter}
        onBlur={handleLeave}
        className={`${base} ${isActive ? active : inactive}`}
      >
        {inner}
      </NavLink>
      {tooltip}
    </>
  );
}
